import React, { useState, useEffect } from 'react';
import { toast } from './Toast';
import { askConfirm } from './ui';
import { crmApi } from '../services/crmApi';
import { Calendar, Plus, Clock, MapPin, User, X, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';

interface Meeting {
  id: number;
  application_id: number | null;
  title: string;
  description: string;
  meeting_date: string;
  duration_minutes: number;
  location: string;
  status: string;
  first_name?: string;
  last_name?: string;
}

interface MeetingsCalendarProps {
  productCode?: string;
}

export const MeetingsCalendar: React.FC<MeetingsCalendarProps> = ({ productCode }) => {
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [showForm, setShowForm] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [form, setForm] = useState({
    title: '',
    description: '',
    meetingDate: '',
    meetingTime: '10:00',
    durationMinutes: 60,
    location: '',
    applicationId: '',
  });

  useEffect(() => {
    loadMeetings();
  }, [currentMonth]);

  const loadMeetings = async () => {
    setLoading(true);
    try {
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth();
      const from = new Date(year, month, 1).toISOString();
      const to = new Date(year, month + 1, 0, 23, 59, 59).toISOString();
      const data = await crmApi.getMeetings({ from, to, serviceType: productCode });
      setMeetings(data.meetings || []);
    } catch (error) {
      console.error('Error loading meetings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const meetingDate = `${form.meetingDate}T${form.meetingTime}:00`;
      await crmApi.createMeeting({
        title: form.title,
        description: form.description,
        meetingDate,
        durationMinutes: form.durationMinutes,
        location: form.location,
        applicationId: form.applicationId ? parseInt(form.applicationId) : undefined,
      });
      setShowForm(false);
      setForm({ title: '', description: '', meetingDate: '', meetingTime: '10:00', durationMinutes: 60, location: '', applicationId: '' });
      loadMeetings();
    } catch (error) {
      console.error('Error creating meeting:', error);
      toast.error('Błąd tworzenia spotkania');
    }
  };

  const handleDelete = async (id: number) => {
    if (!(await askConfirm({ title: 'Usuńac spotkanie?' }))) return;
    try {
      await crmApi.deleteMeeting(id);
      loadMeetings();
    } catch (error) {
      console.error('Error deleting meeting:', error);
    }
  };

  const prevMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  const nextMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));

  // Calendar grid
  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const startOffset = firstDay === 0 ? 6 : firstDay - 1; // Monday start

  const days: (number | null)[] = [];
  for (let i = 0; i < startOffset; i++) days.push(null);
  for (let d = 1; d <= daysInMonth; d++) days.push(d);

  const getMeetingsForDay = (day: number) => {
    return meetings.filter(m => {
      const d = new Date(m.meeting_date);
      return d.getDate() === day && d.getMonth() === month && d.getFullYear() === year;
    });
  };

  const today = new Date();
  const isToday = (day: number) =>
    day === today.getDate() && month === today.getMonth() && year === today.getFullYear();

  const monthName = currentMonth.toLocaleDateString('pl-PL', { month: 'long', year: 'numeric' });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold flex items-center gap-2">
          <Calendar size={24} className="text-blue-600" />
          Kalendarz spotkan
        </h2>
        <button
          type="button"
          onClick={() => {
            setShowForm(true);
            setForm(f => ({ ...f, meetingDate: selectedDate || new Date().toISOString().split('T')[0] }));
          }}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2 text-sm font-medium"
        >
          <Plus size={16} />
          Nowe spotkanie
        </button>
      </div>

      {/* Month navigation */}
      <div className="flex items-center justify-between bg-white rounded-lg shadow p-4">
        <button type="button" onClick={prevMonth} className="p-2 hover:bg-gray-100 rounded-lg">
          <ChevronLeft size={20} />
        </button>
        <h3 className="text-lg font-semibold capitalize">{monthName}</h3>
        <button type="button" onClick={nextMonth} className="p-2 hover:bg-gray-100 rounded-lg">
          <ChevronRight size={20} />
        </button>
      </div>

      {/* Calendar Grid */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {/* Day headers */}
        <div className="grid grid-cols-7 bg-gray-50 border-b">
          {['Pon', 'Wt', 'Sr', 'Czw', 'Pt', 'Sob', 'Ndz'].map(d => (
            <div key={d} className="p-2 text-center text-xs font-medium text-gray-500">{d}</div>
          ))}
        </div>

        {/* Day cells */}
        <div className="grid grid-cols-7">
          {days.map((day, i) => {
            if (!day) return <div key={`empty-${i}`} className="p-2 min-h-[80px] bg-gray-50 border-b border-r" />;

            const dayMeetings = getMeetingsForDay(day);
            const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

            return (
              <div
                key={day}
                className={`p-1.5 min-h-[80px] border-b border-r cursor-pointer hover:bg-blue-50 transition-colors ${
                  isToday(day) ? 'bg-blue-50' : ''
                } ${selectedDate === dateStr ? 'ring-2 ring-blue-400 ring-inset' : ''}`}
                onClick={() => setSelectedDate(dateStr)}
              >
                <div className={`text-xs font-medium mb-1 ${isToday(day) ? 'text-blue-600' : 'text-gray-700'}`}>
                  {day}
                </div>
                {dayMeetings.slice(0, 2).map(m => (
                  <div
                    key={m.id}
                    className={`text-[10px] px-1 py-0.5 rounded mb-0.5 truncate ${
                      m.status === 'anulowane' ? 'bg-red-100 text-red-600 line-through' :
                      m.status === 'zakończone' ? 'bg-green-100 text-green-700' :
                      'bg-blue-100 text-blue-700'
                    }`}
                    title={`${m.title} (${new Date(m.meeting_date).toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })})`}
                  >
                    {new Date(m.meeting_date).toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })} {m.title}
                  </div>
                ))}
                {dayMeetings.length > 2 && (
                  <div className="text-[10px] text-gray-400">+{dayMeetings.length - 2} więcej</div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Selected day meetings list */}
      {selectedDate && (
        <div className="bg-white rounded-lg shadow p-4">
          <h4 className="font-medium text-sm text-gray-700 mb-3">
            Spotkania: {new Date(selectedDate).toLocaleDateString('pl-PL', { weekday: 'long', day: 'numeric', month: 'long' })}
          </h4>
          {getMeetingsForDay(parseInt(selectedDate.split('-')[2])).length > 0 ? (
            <div className="space-y-2">
              {getMeetingsForDay(parseInt(selectedDate.split('-')[2])).map(m => (
                <div key={m.id} className="flex items-start justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <div className="font-medium text-sm">{m.title}</div>
                    <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
                      <span className="flex items-center gap-1">
                        <Clock size={12} />
                        {new Date(m.meeting_date).toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })}
                        ({m.duration_minutes} min)
                      </span>
                      {m.location && (
                        <span className="flex items-center gap-1">
                          <MapPin size={12} />
                          {m.location}
                        </span>
                      )}
                      {m.first_name && (
                        <span className="flex items-center gap-1">
                          <User size={12} />
                          {m.first_name} {m.last_name}
                        </span>
                      )}
                    </div>
                    {m.description && <p className="text-xs text-gray-400 mt-1">{m.description}</p>}
                  </div>
                  <button
                    type="button"
                    onClick={() => handleDelete(m.id)}
                    className="text-gray-400 hover:text-red-500 p-1"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400">Brak spotkan w tym dniu</p>
          )}
        </div>
      )}

      {/* New Meeting Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">Nowe spotkanie</h3>
              <button type="button" onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tytuł *</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                  required
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                  placeholder="np. Spotkanie z klientem"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data *</label>
                  <input
                    type="date"
                    value={form.meetingDate}
                    onChange={e => setForm(f => ({ ...f, meetingDate: e.target.value }))}
                    required
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Godzina *</label>
                  <input
                    type="time"
                    value={form.meetingTime}
                    onChange={e => setForm(f => ({ ...f, meetingTime: e.target.value }))}
                    required
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Czas (min)</label>
                  <select
                    value={form.durationMinutes}
                    onChange={e => setForm(f => ({ ...f, durationMinutes: parseInt(e.target.value) }))}
                    aria-label="Czas trwania"
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                  >
                    <option value={15}>15 min</option>
                    <option value={30}>30 min</option>
                    <option value={45}>45 min</option>
                    <option value={60}>1 godz.</option>
                    <option value={90}>1.5 godz.</option>
                    <option value={120}>2 godz.</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Miejsce</label>
                  <input
                    type="text"
                    value={form.location}
                    onChange={e => setForm(f => ({ ...f, location: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                    placeholder="Biuro / Online"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">ID wniosku (opcjonalne)</label>
                <input
                  type="number"
                  value={form.applicationId}
                  onChange={e => setForm(f => ({ ...f, applicationId: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                  placeholder="np. 24"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Opis</label>
                <textarea
                  value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  rows={2}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                  placeholder="Notatki..."
                />
              </div>
              <div className="flex gap-2 pt-2">
                <button
                  type="submit"
                  className="flex-1 bg-blue-600 text-white py-2 rounded-lg font-medium hover:bg-blue-700"
                >
                  Utwórz spotkanie
                </button>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-4 py-2 border rounded-lg text-gray-600 hover:bg-gray-50"
                >
                  Anuluj
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
